import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toggleDetectionsOpen, toggleSuspectViewerOpen, fetchInspectionSperryFaults, updateSperryFilters } from "../../redux/actions";
import { addDetectionsTypeFilter, setActiveDetectionsTab } from "../../redux/actions/index";
import _ from "lodash";
import { notification } from "antd";

const REPLACE_EXISTING_FILTERS = true;
const detectionTypeFiltersSelector = (state) => state.railInspection.detections.typeFilters;
const classificationsDataSelector = (state) => state.railInspection.classifications.data;
const sessionIDSelector = (state) => state.playlist.data.routeID;
const inspectionPulseCountsSelector = (state) => state.railInspection.railInspectionPulseData;

const validateOptionsObject = (options) => {
    if (typeof options !== "object") {
        return false;
    }

    // Make sure options object has an options string key, and an array of values
    for (let key in options) {
        if (typeof key !== "string" || !Array.isArray(options[key])) {
            return false;
        }
    }

    return true;
};

// Make sure that the filters are cased correctly, regardless of what's passed in the URL. Filter types are always title case.
const ensureTitleCase = (arrayOfStrings) => {
    arrayOfStrings.forEach((string, index) => {
        const splitString = string.split(" ");
        if (splitString.length === 1) {
            arrayOfStrings[index] = string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        } else {
            arrayOfStrings[index] = splitString
                .map((word) => {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                })
                .join(" ");
        }
    });
};

function notifyOptionsParsingError() {
    notification.error({
        message: "Invalid options parameter",
        description: "There was an error parsing the options parameter from the URL. Please ensure it is correctly formatted.",
        duration: 5,
    });
}

const handleSetFilters = (options, dispatch) => {
    // Check if options was passed in the URL, then validate the shape

    let optionsObject = {};

    if (options) {
        try {
            optionsObject = JSON.parse(options);
        } catch (error) {
            console.error("Error parsing options", error);
            notifyOptionsParsingError();
        }
    }

    if (!validateOptionsObject(optionsObject)) {
        notifyOptionsParsingError();
        return;
    }

    const detectionTypeFilters = optionsObject["detection_type"] || [];
    // If there are detection types, apply filters that were passed in
    if (detectionTypeFilters.length) {
        ensureTitleCase(detectionTypeFilters);
        dispatch(addDetectionsTypeFilter(detectionTypeFilters, REPLACE_EXISTING_FILTERS));
    }
};

const useOpenDialogFromURLParams = () => {
    const dispatch = useDispatch();
    const [isReady, setIsReady] = useState(false);
    const filterTypes = useSelector(detectionTypeFiltersSelector);
    const classificationsData = useSelector(classificationsDataSelector);
    const sessionID = useSelector(sessionIDSelector);
    const inspectionPulseCounts = useSelector(inspectionPulseCountsSelector);

    // Maps the values of the comma separated 'select' query param to the function that will handle each dialog. Can be extended for other things as well.
    const paramToDispatchMap = {
        detections: (options) => {
            // Detections are the default active tab, so no need to set it here
            dispatch(toggleDetectionsOpen());
            handleSetFilters(options, dispatch);
        },
        classifications: (options) => {
            dispatch(toggleDetectionsOpen());
            dispatch(setActiveDetectionsTab("Classifications"));
            handleSetFilters(options, dispatch);
        },
        utu_suspects: (options) => {
            let optionsObject = {};
            dispatch(toggleSuspectViewerOpen());

            try {
                optionsObject = JSON.parse(options);
            } catch (error) {
                console.error("Error parsing options:", error);
                notifyOptionsParsingError();
                return;
            }

            const optionsObjectKeys = Object.keys(optionsObject);

            // If suspect_id was passed in the options, and matches a valid fault, navigate to that fault
            if (optionsObjectKeys.length && optionsObjectKeys.includes("suspect_id")) {
                dispatch(updateSperryFilters(optionsObject.suspect_id));
            }
        },
    };

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    // Need this useEffect to trigger the one below when Redux has populated the filter store or classifications data if classifications passed as selected
    useEffect(() => {
        let select = queryParams.get("selected");
        if (select) {
            const dialogs = select.split(",");
            const lowerCaseDialogs = dialogs.map((str) => str.toLowerCase());

            // if selected = detections check if filters populated
            if (!isReady && _.includes(lowerCaseDialogs, "detections") && filterTypes.length) {
                setIsReady(true);
            }

            // if selected = classifications in url check if classifications data in redux
            if (!isReady && _.includes(lowerCaseDialogs, "classifications") && classificationsData.length) {
                setIsReady(true);
            }

            // if selected = utu_suspects in url
            if (!isReady && _.includes(lowerCaseDialogs, "utu_suspects") && Object.keys(inspectionPulseCounts).length > 0) {
                dispatch(fetchInspectionSperryFaults(sessionID)).then(() => {
                    setIsReady(true);
                });
            }
        }
    }, [filterTypes, classificationsData, isReady, sessionID, inspectionPulseCounts]);

    useEffect(() => {
        const select = queryParams.get("selected");
        if (select && isReady) {
            const dialogs = select.split(",");
            if (dialogs.length) {
                dialogs.forEach((dialog) => {
                    const handler = paramToDispatchMap[dialog.toLowerCase()];
                    const options = queryParams.get("options");
                    if (handler) {
                        handler(options);
                    }
                });
            }
        }
    }, [isReady]);
};

export default useOpenDialogFromURLParams;
